import { useEffect, useState } from "react";
import * as React from "react";
import { toast } from "sonner";
import { Building2, Database, Download, FlaskConical, Plus, ShieldCheck, Trash2, Upload, Users } from "lucide-react";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useCompany } from "../context/CompanyContext";
import { PageHeader, TableSkeleton, fmtDate } from "../components/erp";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "../components/ui/alert-dialog";

const ROLES = ["super_admin", "admin", "accounts_manager", "sales_manager", "purchase_manager",
  "inventory_manager", "hr_manager", "employee", "view_only"];

function CompanyTab() {
  const { active, reload } = useCompany();
  const { user } = useAuth();
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signatureBusy, setSignatureBusy] = useState(false);
  const [signatureFile, setSignatureFile] = useState(null);
  const [signaturePreview, setSignaturePreview] = useState("");

  useEffect(() => {
    if (active) {
      setForm({ ...active, invoice_prefix: active.prefixes?.sales_invoice || "", payment_prefix: active.prefixes?.payment_in || "" });
      setSignatureFile(null);
      setSignaturePreview(active.signature?.file_name ? `/api/companies/${active.id}/signature-file/${encodeURIComponent(active.signature.file_name)}` : "");
    }
  }, [active]);

  if (!form) return <TableSkeleton rows={3} />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/companies/${active.id}`, form);
      toast.success("Company profile saved");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const uploadSignature = async () => {
    if (!signatureFile) { toast.error("Select a PNG signature file first"); return; }
    if (signatureFile.type !== "image/png") { toast.error("Signature must be a PNG file"); return; }
    if (signatureFile.size > 2 * 1024 * 1024) { toast.error("Signature must be 2 MB or smaller"); return; }
    setSignatureBusy(true);
    try {
      const fd = new FormData();
      fd.append("signature", signatureFile);
      const { data } = await api.post(`/companies/${active.id}/signature`, fd);
      setSignaturePreview(data.signature.url);
      setSignatureFile(null);
      toast.success("Authorized signature uploaded");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setSignatureBusy(false); }
  };

  const removeSignature = async () => {
    setSignatureBusy(true);
    try {
      await api.delete(`/companies/${active.id}/signature`);
      setSignaturePreview("");
      setSignatureFile(null);
      toast.success("Authorized signature removed");
      reload();
    } catch (e) { toast.error(errMsg(e)); } finally { setSignatureBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 max-w-3xl" data-testid="company-settings">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2"><Label>Company Name</Label><Input data-testid="company-name" className="mt-1.5" value={form.name} onChange={(e) => set("name", e.target.value)} /></div>
        <div><Label>Code</Label><Input className="mt-1.5 font-mono" value={form.code} onChange={(e) => set("code", e.target.value.toUpperCase())} /></div>
        <div><Label>GSTIN</Label><Input data-testid="company-gstin" className="mt-1.5 font-mono" value={form.gstin || ""} onChange={(e) => set("gstin", e.target.value.toUpperCase())} /></div>
        <div><Label>PAN</Label><Input className="mt-1.5 font-mono" value={form.pan || ""} onChange={(e) => set("pan", e.target.value.toUpperCase())} /></div>
        <div><Label>State</Label><Input className="mt-1.5" value={form.state || ""} onChange={(e) => set("state", e.target.value)} /></div>
        <div className="sm:col-span-2"><Label>Address</Label><Input className="mt-1.5" value={form.address || ""} onChange={(e) => set("address", e.target.value)} /></div>
        <div><Label>Email</Label><Input type="email" className="mt-1.5" value={form.email || ""} onChange={(e) => set("email", e.target.value)} /></div>
        <div><Label>Phone</Label><Input className="mt-1.5" value={form.phone || ""} onChange={(e) => set("phone", e.target.value)} /></div>
        <div><Label>Invoice Prefix</Label><Input data-testid="company-invoice-prefix" className="mt-1.5 font-mono" value={form.invoice_prefix} onChange={(e) => set("invoice_prefix", e.target.value)} /></div>
        <div><Label>Payment Prefix</Label><Input className="mt-1.5 font-mono" value={form.payment_prefix} onChange={(e) => set("payment_prefix", e.target.value)} /></div>
        <div><Label>Bank Name</Label><Input className="mt-1.5" value={form.bank_name || ""} onChange={(e) => set("bank_name", e.target.value)} /></div>
        <div><Label>Account No / IFSC</Label><div className="flex gap-2 mt-1.5"><Input value={form.bank_account || ""} onChange={(e) => set("bank_account", e.target.value)} /><Input className="font-mono" value={form.bank_ifsc || ""} onChange={(e) => set("bank_ifsc", e.target.value.toUpperCase())} /></div></div>
        <div><Label>UPI ID</Label><Input className="mt-1.5 font-mono" value={form.upi || ""} onChange={(e) => set("upi", e.target.value)} /></div>
      </div>

      {user?.is_super_admin && (
        <div className="mt-6 rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/40 p-4" data-testid="authorized-signature-section">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="font-semibold text-sm">Authorized Signature</p>
              <p className="text-xs text-slate-500 mt-1">Upload a transparent PNG. It will appear automatically on sales invoices and their Print / PDF output.</p>
            </div>
            {signaturePreview && <img src={signaturePreview} alt="Authorized signature" className="h-16 max-w-[220px] object-contain rounded border border-slate-200 dark:border-slate-700 bg-white p-2" />}
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Input data-testid="signature-file-input" type="file" accept="image/png,.png" className="max-w-sm" onChange={(e) => { const f=e.target.files?.[0]||null; setSignatureFile(f); if(f) setSignaturePreview(URL.createObjectURL(f)); }} />
            <Button type="button" data-testid="signature-upload-btn" onClick={uploadSignature} disabled={signatureBusy || !signatureFile}><Upload className="mr-1.5 h-4 w-4" />{signatureBusy ? "Uploading..." : "Upload Signature"}</Button>
            {active?.signature?.file_name && <Button type="button" variant="outline" data-testid="signature-remove-btn" onClick={removeSignature} disabled={signatureBusy}><Trash2 className="mr-1.5 h-4 w-4" />Remove</Button>}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">PNG only · maximum 2 MB · Super Admin only</p>
        </div>
      )}

      <Button data-testid="company-save-btn" onClick={save} disabled={busy} className="mt-5 bg-slate-900 dark:bg-slate-100 dark:text-slate-900">{busy ? "Saving..." : "Save Company Profile"}</Button>
    </div>
  );
}
function UsersTab() {
  const { companies } = useCompany();
  const { user } = useAuth();
  const [users, setUsers] = useState(null);
  const [roles, setRoles] = useState([]);
  const [dialog, setDialog] = useState({ open: false, user: null });
  const [form, setForm] = useState({ name: "", email: "", password: "", status: "active", access: {} });

  const load = () => {
    api.get("/users").then((r) => setUsers(r.data.items)).catch((e) => toast.error(errMsg(e)));
    api.get("/roles").then((r) => setRoles(r.data.items)).catch(() => {});
  };
  useEffect(load, []); // eslint-disable-line

  const openNew = () => {
    setForm({ name: "", email: "", password: "", status: "active", access: {} });
    setDialog({ open: true, user: null });
  };
  const openEdit = (u) => {
    const access = {};
    (u.companies || []).forEach((c) => (access[c.company_id] = c.role));
    setForm({ name: u.name, email: u.email, password: "", status: u.status || "active", access });
    setDialog({ open: true, user: u });
  };

  const save = async () => {
    const companyAccess = Object.entries(form.access)
      .filter(([, role]) => role && role !== "none")
      .map(([company_id, role]) => ({ company_id, role }));
    try {
      if (dialog.user) {
        await api.put(`/users/${dialog.user.id}`, { name: form.name, email: form.email,
          password: form.password || null, status: form.status, companies: companyAccess });
        toast.success("User updated");
      } else {
        await api.post("/users", { name: form.name, email: form.email, password: form.password,
          status: form.status, companies: companyAccess });
        toast.success("User created");
      }
      setDialog({ open: false, user: null });
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  if (!users) return <TableSkeleton />;
  return (
    <div>
      <div className="mb-3 flex justify-end">
        <Button data-testid="new-user-btn" onClick={openNew} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">
          <Plus className="mr-1.5 h-4 w-4" /> New User
        </Button>
      </div>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto">
        <table className="w-full text-sm" data-testid="users-table">
          <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
            <th className="p-3">User</th><th className="p-3">Role (this company)</th><th className="p-3">Company Access</th><th className="p-3">Status</th><th className="p-3"></th></tr></thead>
          <tbody>
            {users.map((u) => (
              <tr key={u.id} className="border-b border-slate-100 dark:border-slate-800">
                <td className="p-3"><p className="font-medium">{u.name}</p><p className="text-xs text-slate-500">{u.email}</p></td>
                <td className="p-3"><Badge variant="secondary" className="capitalize">{(u.role_in_company || "").replace(/_/g, " ")}</Badge></td>
                <td className="p-3 text-xs text-slate-500">{u.is_super_admin ? "All companies" : `${(u.companies || []).length} companies`}</td>
                <td className="p-3"><Badge className={u.status === "deactivated" ? "bg-red-100 text-red-700 border-0" : "bg-emerald-100 text-emerald-700 border-0"}>{u.status || "active"}</Badge></td>
                <td className="p-3"><Button size="sm" variant="ghost" data-testid={`edit-user-${u.email}`} onClick={() => openEdit(u)}>Edit</Button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={dialog.open} onOpenChange={() => setDialog({ open: false, user: null })}>
        <DialogContent aria-describedby={undefined} className="max-w-lg bg-white dark:bg-slate-900" data-testid="user-dialog">
          <DialogHeader><DialogTitle className="font-display">{dialog.user ? "Edit User" : "New User"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input data-testid="user-name" className="mt-1.5" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} /></div>
            <div><Label>Email *</Label><Input data-testid="user-email" type="email" className="mt-1.5" disabled={!!dialog.user} value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} /></div>
            <div><Label>{dialog.user ? "Reset Password (optional)" : "Password *"}</Label><Input data-testid="user-password" type="password" className="mt-1.5" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} /></div>
            <div><Label>Status</Label>
              <Select value={form.status} onValueChange={(v) => setForm((f) => ({ ...f, status: v }))}>
                <SelectTrigger data-testid="user-status" className="mt-1.5"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="deactivated">Deactivated</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div className="mt-4">
            <Label className="font-semibold">Company Access & Roles</Label>
            <div className="mt-2 space-y-2">
              {companies.map((c) => (
                <div key={c.company_id} className="flex items-center gap-3 rounded-lg border border-slate-200 dark:border-slate-700 p-2.5">
                  <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-[10px] font-bold text-white" style={{ background: c.accent }}>{c.code.slice(0, 2)}</span>
                  <span className="flex-1 text-sm truncate">{c.name}</span>
                  <Select value={form.access[c.company_id] || "none"}
                    onValueChange={(v) => setForm((f) => ({ ...f, access: { ...f.access, [c.company_id]: v } }))}>
                    <SelectTrigger data-testid={`user-role-${c.code.toLowerCase()}`} className="w-44 h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No Access</SelectItem>
                      {ROLES.filter((r) => r !== "super_admin").map((r) => (
                        <SelectItem key={r} value={r} className="capitalize">{r.replace(/_/g, " ")}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              ))}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog({ open: false, user: null })}>Cancel</Button>
            <Button data-testid="user-save-btn" onClick={save} className="bg-slate-900 dark:bg-slate-100 dark:text-slate-900">Save User</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function RolesTab() {
  const [roles, setRoles] = useState(null);
  useEffect(() => { api.get("/roles").then((r) => setRoles(r.data.items)).catch(() => setRoles([])); }, []);
  if (!roles) return <TableSkeleton />;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="roles-table">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
          <th className="p-3">Role</th><th className="p-3">Permissions</th></tr></thead>
        <tbody>
          {roles.map((r) => (
            <tr key={r.key} className="border-b border-slate-100 dark:border-slate-800">
              <td className="p-3 font-medium whitespace-nowrap">{r.label}</td>
              <td className="p-3 text-xs text-slate-500 font-mono">
                {r.permissions["*"]?.includes("*") ? "Full access to all modules" :
                  Object.entries(r.permissions).map(([m, acts]) => `${m}: ${acts.join("/")}`).join(" · ")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditTab() {
  const [logs, setLogs] = useState(null);
  useEffect(() => { api.get("/audit-logs?limit=100").then((r) => setLogs(r.data.items)).catch(() => setLogs([])); }, []);
  if (!logs) return <TableSkeleton />;
  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 overflow-x-auto" data-testid="audit-table">
      <table className="w-full text-sm">
        <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
          <th className="p-3">Time</th><th className="p-3">User</th><th className="p-3">Action</th><th className="p-3">Module</th><th className="p-3">Description</th></tr></thead>
        <tbody>
          {logs.length === 0 && <tr><td colSpan={5} className="p-10 text-center text-sm text-slate-400">No audit entries yet</td></tr>}
          {logs.map((l) => (
            <tr key={l.id} className="border-b border-slate-100 dark:border-slate-800">
              <td className="p-3 font-mono text-xs text-slate-500 whitespace-nowrap">{fmtDate(l.created_at)}</td>
              <td className="p-3">{l.user_name || l.user_email}</td>
              <td className="p-3"><Badge variant="secondary" className="capitalize text-[10px]">{l.action}</Badge></td>
              <td className="p-3 capitalize text-slate-500">{l.module}</td>
              <td className="p-3 text-slate-600 dark:text-slate-300">{l.description}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function DemoTab() {
  const { user } = useAuth();
  const [busy, setBusy] = useState("");
  if (!user?.is_super_admin) return <p className="text-sm text-slate-500 p-4">Only Super Admin can manage demo data.</p>;
  const run = async (what) => {
    setBusy(what);
    try {
      const { data } = await api.post(`/admin/demo/${what}`);
      toast.success(what === "seed" ? "Demo data loaded for all companies" : `Demo data removed: ${JSON.stringify(data.removed)}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl border border-amber-200 dark:border-amber-500/30 bg-amber-50 dark:bg-amber-500/5 p-5 max-w-2xl" data-testid="demo-data-panel">
      <p className="font-display font-semibold flex items-center gap-2"><FlaskConical className="h-4 w-4 text-amber-500" /> Demo / Training Data</p>
      <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
        Loads clearly-flagged sample customers, suppliers, items, invoices and payments into every company.
        Demo records never mix with real data and can be wiped in one click.
      </p>
      <div className="mt-4 flex gap-2">
        <Button data-testid="seed-demo-btn" disabled={!!busy} onClick={() => run("seed")}
          className="bg-amber-600 hover:bg-amber-700 text-white"><Database className="mr-1.5 h-4 w-4" /> {busy === "seed" ? "Loading..." : "Load Demo Data"}</Button>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button variant="outline" data-testid="remove-demo-btn" className="border-red-300 text-red-600"><Trash2 className="mr-1.5 h-4 w-4" /> Remove Demo Data</Button>
          </AlertDialogTrigger>
          <AlertDialogContent aria-describedby={undefined} className="bg-white dark:bg-slate-900">
            <AlertDialogHeader>
              <AlertDialogTitle>Remove all demo data?</AlertDialogTitle>
              <AlertDialogDescription>Deletes every record flagged as demo across all companies. Real records are untouched.</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Keep</AlertDialogCancel>
              <AlertDialogAction data-testid="remove-demo-confirm" onClick={() => run("remove")} className="bg-red-600 hover:bg-red-700">Remove demo data</AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </div>
  );
}

function ImportTab() {
  const [kind, setKind] = useState("parties");
  const [csvText, setCsvText] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = React.useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => setCsvText(String(reader.result || ""));
    reader.readAsText(f);
  };

  const run = async () => {
    if (!csvText.trim()) { toast.error("Choose a CSV file or paste CSV content"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/import/${kind}`, { csv_text: csvText });
      setResult(data);
      toast.success(`Imported ${data.imported} ${kind}${data.errors.length ? ` (${data.errors.length} skipped)` : ""}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5 max-w-3xl" data-testid="import-panel">
      <div className="flex flex-wrap gap-2 mb-4">
        {["parties", "items"].map((k) => (
          <Button key={k} size="sm" variant={kind === k ? "default" : "outline"} data-testid={`import-kind-${k}`}
            onClick={() => { setKind(k); setResult(null); }}
            className={kind === k ? "bg-slate-900 dark:bg-slate-100 dark:text-slate-900" : ""}>
            Import {k}
          </Button>
        ))}
        <input ref={fileRef} type="file" accept=".csv" className="hidden" onChange={onFile} />
        <Button size="sm" variant="outline" data-testid="import-file-btn" onClick={() => fileRef.current?.click()}>
          <Upload className="mr-1.5 h-3.5 w-3.5" /> Choose CSV file
        </Button>
      </div>
      <p className="text-xs text-slate-500 mb-2 font-mono">
        {kind === "parties"
          ? "Columns: name, type, mobile, email, gstin, city, state, opening_balance"
          : "Columns: name, type, code, hsn, unit, purchase_price, sale_price, tax_rate, opening_stock, min_stock"}
      </p>
      <textarea data-testid="import-csv-text" rows={6} value={csvText} onChange={(e) => setCsvText(e.target.value)}
        placeholder="Paste CSV content here..."
        className="w-full rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-950 p-3 text-xs font-mono outline-none focus:ring-1 focus:ring-slate-400" />
      <Button data-testid="import-run-btn" onClick={run} disabled={busy} className="mt-3 bg-slate-900 dark:bg-slate-100 dark:text-slate-900">
        {busy ? "Importing..." : "Validate & Import"}
      </Button>
      {result && (
        <div className="mt-4" data-testid="import-result">
          <p className="text-sm font-medium">Imported: <b className="text-emerald-600">{result.imported}</b> · Skipped: <b className={result.errors.length ? "text-red-600" : ""}>{result.errors.length}</b></p>
          {result.errors.length > 0 && (
            <ul className="mt-2 max-h-40 overflow-y-auto rounded-lg bg-red-50 dark:bg-red-500/10 p-3 text-xs text-red-700 dark:text-red-300 space-y-1">
              {result.errors.map((e, i) => <li key={i}>{e}</li>)}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

function BackupTab() {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const [dump, setDump] = useState(null);
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const restoreRef = React.useRef(null);
  if (!user?.is_super_admin) return <p className="text-sm text-slate-500 p-4">Only Super Admin can manage backups.</p>;

  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get("/admin/backup/export", { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(new Blob([r.data], { type: "application/json" }));
      link.download = `krishna_backup_${new Date().toISOString().slice(0, 10)}.json`;
      link.click();
      toast.success("Backup downloaded");
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const onRestoreFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(String(reader.result || ""));
        if (parsed.app !== "krishna-group-bms" || !parsed.data || typeof parsed.data !== "object") {
          throw new Error("invalid");
        }
        setDump(parsed);
        setRestoreResult(null);
      } catch {
        toast.error("Not a valid Krishna Group backup file");
      }
    };
    reader.readAsText(f);
    e.target.value = "";
  };

  const runRestore = async () => {
    setRestoring(true);
    try {
      const { data } = await api.post("/admin/backup/restore", { data: dump.data });
      setRestoreResult(data);
      setDump(null);
      toast.success("Backup restored — please sign in again");
      setTimeout(async () => {
        try { await api.post("/auth/logout"); } catch { /* noop */ }
        localStorage.removeItem("kg_company");
        window.location.href = "/login";
      }, 2500);
    } catch (e) { toast.error(errMsg(e)); } finally { setRestoring(false); }
  };

  const dumpCounts = dump ? Object.entries(dump.data).filter(([, v]) => Array.isArray(v)) : [];

  return (
    <div className="space-y-5 max-w-2xl">
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5" data-testid="backup-panel">
        <p className="font-display font-semibold flex items-center gap-2"><Database className="h-4 w-4" /> Full Data Backup</p>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Downloads a complete JSON snapshot of all companies, transactions, masters and ledgers. Store it securely offsite.
        </p>
        <Button data-testid="backup-download-btn" onClick={download} disabled={busy} className="mt-4 bg-slate-900 dark:bg-slate-100 dark:text-slate-900">
          <Download className="mr-1.5 h-4 w-4" /> {busy ? "Preparing..." : "Download Backup (JSON)"}
        </Button>
      </div>

      <div className="rounded-xl border border-red-200 dark:border-red-500/30 bg-white dark:bg-slate-900 p-5" data-testid="restore-panel">
        <p className="font-display font-semibold flex items-center gap-2"><Upload className="h-4 w-4 text-red-500" /> Restore from Backup</p>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
          Upload a previously downloaded backup file. <b className="text-red-600 dark:text-red-400">This permanently replaces ALL current data</b> — including every company, transaction and ledger.
        </p>
        <input ref={restoreRef} type="file" accept=".json,application/json" className="hidden" onChange={onRestoreFile} />
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <Button variant="outline" data-testid="restore-file-btn" onClick={() => restoreRef.current?.click()}>
            <Upload className="mr-1.5 h-4 w-4" /> Choose backup file
          </Button>
          {dump && (
            <span className="text-xs text-slate-500" data-testid="restore-file-summary">
              Backup from {fmtDate(dump.exported_at)} · {dumpCounts.reduce((s, [, v]) => s + v.length, 0)} records in {dumpCounts.length} collections
            </span>
          )}
        </div>
        {dump && (
          <div className="mt-4" data-testid="restore-preview">
            <div className="max-h-44 overflow-y-auto rounded-lg border border-slate-200 dark:border-slate-700">
              <table className="w-full text-xs">
                <tbody>
                  {dumpCounts.map(([coll, docs]) => (
                    <tr key={coll} className="border-b border-slate-100 dark:border-slate-800 last:border-0">
                      <td className="p-2 font-mono text-slate-500">{coll}</td>
                      <td className="p-2 text-right font-semibold">{docs.length}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button data-testid="restore-run-btn" disabled={restoring}
                  className="mt-3 bg-red-600 hover:bg-red-700 text-white">
                  {restoring ? "Restoring..." : "Restore & Replace All Data"}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent aria-describedby={undefined} className="bg-white dark:bg-slate-900">
                <AlertDialogHeader>
                  <AlertDialogTitle>Replace all current data?</AlertDialogTitle>
                  <AlertDialogDescription>
                    Every current record across all companies will be deleted and replaced by this backup. This cannot be undone. Consider downloading a fresh backup first.
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                  <AlertDialogAction data-testid="restore-confirm-btn" onClick={runRestore} className="bg-red-600 hover:bg-red-700">Yes, restore backup</AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
        {restoreResult && (
          <p className="mt-3 text-sm text-emerald-600" data-testid="restore-result">
            Restored {Object.values(restoreResult.restored).reduce((s, n) => s + n, 0)} records
            ({restoreResult.counters_rebuilt} numbering counters rebuilt).
          </p>
        )}
      </div>
    </div>
  );
}

export default function Settings() {
  const { role } = useCompany();
  const { user } = useAuth();
  const canManage = user?.is_super_admin || role === "admin";
  return (
    <div data-testid="settings-page">
      <PageHeader title="Settings" testId="settings-title" sub="Company profile, users, roles, audit and data tools" />
      <Tabs defaultValue="company">
        <TabsList className="mb-4 flex-wrap h-auto">
          <TabsTrigger value="company" data-testid="tab-company"><Building2 className="mr-1.5 h-3.5 w-3.5" /> Company</TabsTrigger>
          {canManage && <TabsTrigger value="users" data-testid="tab-users"><Users className="mr-1.5 h-3.5 w-3.5" /> Users</TabsTrigger>}
          <TabsTrigger value="roles" data-testid="tab-roles"><ShieldCheck className="mr-1.5 h-3.5 w-3.5" /> Roles</TabsTrigger>
          <TabsTrigger value="audit" data-testid="tab-audit">Audit Log</TabsTrigger>
          {canManage && <TabsTrigger value="import" data-testid="tab-import"><Upload className="mr-1.5 h-3.5 w-3.5" /> Import</TabsTrigger>}
          {user?.is_super_admin && <TabsTrigger value="backup" data-testid="tab-backup"><Download className="mr-1.5 h-3.5 w-3.5" /> Backup</TabsTrigger>}
          {user?.is_super_admin && <TabsTrigger value="demo" data-testid="tab-demo"><FlaskConical className="mr-1.5 h-3.5 w-3.5" /> Demo Data</TabsTrigger>}
        </TabsList>
        <TabsContent value="company"><CompanyTab /></TabsContent>
        {canManage && <TabsContent value="users"><UsersTab /></TabsContent>}
        <TabsContent value="roles"><RolesTab /></TabsContent>
        <TabsContent value="audit"><AuditTab /></TabsContent>
        {canManage && <TabsContent value="import"><ImportTab /></TabsContent>}
        {user?.is_super_admin && <TabsContent value="backup"><BackupTab /></TabsContent>}
        {user?.is_super_admin && <TabsContent value="demo"><DemoTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
